<script lang="ts">
import { computed, defineComponent, toRef, unref } from 'vue';

import { makeTagProps, useNestedGroupActivator, useNestedItem } from '../../composable';
import { CdsTransitionExpand } from '../../transitions';

import { createList, useList } from './composable';

import type { TCdsIconsName } from '@central-design-system/icons';
import type { PropType } from 'vue';

const CdsListGroupActivator = defineComponent({
  name: 'CdsListGroupActivator',
  setup(_, { slots }) {
    useNestedGroupActivator();
    return () => slots.default?.();
  }
});

export const componentName = 'CdsListGroup';
export default defineComponent({
  name: componentName,
  components: { CdsListGroupActivator, CdsTransitionExpand },
  props: {
    /**
     * Заголовок группы списка
     */
    title: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Иконка свернутой группы списка
     */
    collapseIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'chevron-up'
    },
    /**
     * Иконка развернутой группы списка
     */
    expandIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'chevron-down'
    },
    /**
     * Значение группы списка
     */
    value: {
      type: [String, Number] as PropType<string | number>,
      default: undefined
    },
    /**
     * Группа списка на всю ширину
     */
    fluid: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Подгруппа списка
     */
    subgroup: {
      type: Boolean as PropType<boolean>,
      default: false
    },

    ...makeTagProps()
  },
  setup(props, { expose }) {
    const { isOpen, open, id: _id } = useNestedItem(toRef(props, 'value'), true);
    const list = useList();
    const id = computed(() => `cds-list-group--id-${String(_id.value)}`);
    const toggleIcon = computed(() => (unref(isOpen) ? props.collapseIcon : props.expandIcon));
    const isExpandable = computed(() => !props.subgroup);
    const activatorProps = computed(() => ({
      onClick,
      id: unref(id),
      appendIcon: unref(toggleIcon),
      class: 'cds-list-group__header',
      isClickable: unref(isExpandable)
    }));
    const classes = computed(() => ({
      'cds-list-group--open': unref(isOpen),
      'cds-list-group--prepend': unref(list?.hasPrepend),
      'cds-list-group--fluid': props.fluid,
      'cds-list-group--subgroup': props.subgroup
    }));

    createList();

    function onClick(event: Event) {
      open(!unref(isOpen), event);
    }

    expose({
      id,
      isOpen,
      isExpandable
    });

    return {
      id,
      isOpen,
      activatorProps,
      isExpandable,
      classes
    };
  }
});
</script>

<template>
  <component :is="tag" class="cds-list-group" :class="classes">
    <div v-if="!!title" class="cds-list-group__title">
      <!--
        @slot Заголовок группы списка
        @type string | html
      -->
      <slot name="title" />
      <span>{{ title }}</span>
    </div>
    <cds-list-group-activator>
      <!--
        @slot Элемент активатор группы списка
        @type CdsListItem
      -->
      <slot name="activator" :props="activatorProps" :is-open="isOpen" />
    </cds-list-group-activator>
    <template v-if="isExpandable">
      <cds-transition-expand>
        <div v-show="isOpen" class="cds-list-group__items" role="group" :aria-labelledby="id">
          <!--
            @slot Элемент списка
            @type CdsListItem
          -->
          <slot />
        </div>
      </cds-transition-expand>
    </template>
    <template v-else>
      <div class="cds-list-group__items" role="group" :aria-labelledby="id">
        <slot />
      </div>
    </template>
  </component>
</template>
